// M12 round five: "Connect your site", ported from the prototype's ConnectScreen (round zero) and wired to the API. The plugin opens
// /connect with everything after `#` (never sent to a server or a log): the site, its title, the admin's address, the scopes and events, the
// way back, a state and a PKCE challenge. The page leads with the site's host, signs the person in by code (address prefilled) or, for a
// newcomer, opens the creation form with the site's name (M11's own route and controls), then asks where the site's questions go: the
// workspace, the project (or a new one, the plan's limit said first), whether the site should answer customer questions another system
// answers today, and shows what the site may do. Approve sends the browser back to the site with a one-time code; the key never travels in
// an address. No shell, like the sign-in. Only `?step=code` is in the query; the address stays in the page. The sidebar and consent round
// (2026-10-24): the decision is the kit's ConsentPage ("Let <site> into <workspace>?", the choosers kept as the browser's own drop-downs the
// plugin's flows pick, the scopes in plain words with their codes in the hover, the events, the site's strip, "Not you?").
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { Button, ConsentPage, Icon, Input, Logo, Notice } from "@omnabase/ui";
import { PUBLIC_API_SCOPE_LABELS, endpointLabel, factsHolder, siteLabel, parseConnect, projectRoom, returnCancelled, soleHolderProblem, takeoverEmpties, webhookEventLabel, type ConnectApproved, type ConnectRequest, type Me, type Site, type Workspace } from "@omnabase/shared";
import { authClient } from "../lib/auth";
import { ApiError, api, fetchWorkspace } from "../lib/api";
import { isCode, isEmail, normaliseCode, problemFor } from "../lib/signin";
import { loadTurnstile, turnstileToken } from "../lib/turnstile";
import { capsOf } from "../lib/access";
import { NewWorkspaceStep } from "./signin/SignInSteps";
import { signOutHere } from "../lib/push";

export function ConnectScreen({ me, onSignedIn }: { me: Me | null; onSignedIn: () => Promise<void> }) {
  const { hash } = useLocation();
  const parsed = parseConnect(hash);
  return (
    <div className="ob-signin">
      <aside className="ob-signin__aside" aria-hidden="true">
        <div className="ob-signin__aside-inner">
          <span className="ob-signin__logo"><Logo /></span>
          <h2 className="ob-signin__claim">Connect your site.</h2>
          <p className="ob-signin__line">Its questions arrive in your inbox, and your answers go back to it.</p>
        </div>
      </aside>
      <main className="ob-signin__main" aria-label="Connect your site">
        <div className="ob-signin__box ob-signin__box--consent">
          {"problem" in parsed ? <BadLink problem={parsed.problem} /> : <Steps me={me} request={parsed.request} onSignedIn={onSignedIn} />}
        </div>
      </main>
    </div>
  );
}

/** signed out: the address, then the code; signed in without a workspace: the creation form (each under the site's host); else the decision
 *  (the consent page, whose title names the site) */
function Steps({ me, request: r, onSignedIn }: { me: Me | null; request: ConnectRequest; onSignedIn: () => Promise<void> }) {
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState(r.email);
  const [madeHere, setMadeHere] = useState<string | null>(null); // the workspace this flow just made: its first project becomes the site's (2026-10-24)
  const step = (s: "code" | null) => navigate({ pathname: "/connect", search: s ? `?step=${s}` : "", hash }); // the link's data stays after `#`
  if (!me) return <><SiteHead request={r} />{params.get("step") === "code" && email ? <CodeStep email={email} onBack={() => step(null)} onDone={async () => { await onSignedIn(); step(null); }} /> : <EmailStep initial={email} onSent={(e) => { setEmail(e); step("code"); }} />}</>;
  if (!me.workspaces.length) return <><SiteHead request={r} /><NewWorkspaceStep initialName={r.name} onCreated={(slug) => { setMadeHere(slug); void onSignedIn(); }} onCancel={() => window.location.assign(returnCancelled(r))} /></>;
  return <Approve me={me} request={r} madeHere={madeHere} />;
}

/** the site first: its address in large type, its own name under it */
function SiteHead({ request: r }: { request: ConnectRequest }) {
  return (
    <div className="ob-connect__site">
      <span className="ob-connect__mark"><Icon name="web" /></span>
      <div><h1 className="ob-connect__host">{labelOf(r)}</h1><div className="ob-connect__name">{r.name} wants to connect to OmnaBase</div></div>
    </div>
  );
}

function BadLink({ problem }: { problem: string }) {
  return (
    <div className="ob-signin__form">
      <h1 className="ob-signin__title">This link cannot connect a site</h1>
      <p className="ob-signin__intro">{problem}</p>
      <p className="ob-signin__foot">Start again from your site&apos;s OmnaBase settings.</p>
    </div>
  );
}

function EmailStep({ initial, onSent }: { initial: string; onSent: (email: string) => void }) {
  const [email, setEmail] = useState(initial);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const widget = useRef<HTMLDivElement>(null);
  useEffect(() => { void loadTurnstile(); }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isEmail(email)) { setProblem("Enter your email address."); return; }
    setBusy(true); setProblem(null);
    const token = widget.current ? await turnstileToken(widget.current) : undefined;
    const r = await authClient.emailOtp.sendVerificationOtp({ email: email.trim(), type: "sign-in", fetchOptions: { headers: token ? { "x-turnstile-token": token } : {} } });
    setBusy(false);
    if (r.error) { setProblem(problemFor(r.error.status, r.error.code)); return; }
    onSent(email.trim());
  };
  return (
    <form className="ob-signin__form" onSubmit={(e) => void submit(e)} noValidate>
      <p className="ob-signin__intro">Sign in to choose where its questions go. New to OmnaBase? Enter your email, type the code we send you, and set up your first workspace.</p>
      <label className="ob-signin__field"><span className="ob-signin__label">Email</span><Input className="ob-input--text" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" autoFocus /></label>
      <div ref={widget} className="ob-signin__turnstile" />
      {problem && <p className="ob-signin__problem" role="alert">{problem}</p>}
      <Button variant="primary" type="submit" className="ob-signin__btn" disabled={busy}>Send me a code</Button>
    </form>
  );
}

function CodeStep({ email, onBack, onDone }: { email: string; onBack: () => void; onDone: () => Promise<void> }) {
  const [code, setCode] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isCode(code)) { setProblem("Enter the six digits from the mail."); return; }
    setBusy(true); setProblem(null);
    const r = await authClient.signIn.emailOtp({ email, otp: normaliseCode(code) });
    if (r.error) { setBusy(false); setProblem(problemFor(r.error.status, r.error.code)); return; }
    await onDone();
  };
  return (
    <form className="ob-signin__form" onSubmit={(e) => void submit(e)} noValidate>
      <p className="ob-signin__intro">We sent a six-digit code to <strong>{email}</strong>. It is good for ten minutes.</p>
      <label className="ob-signin__field"><span className="ob-signin__label">Code</span><Input className="ob-input--text ob-signin__code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123 456" aria-label="Code" autoFocus /></label>
      {problem && <p className="ob-signin__problem" role="alert">{problem}</p>}
      <Button variant="primary" type="submit" className="ob-signin__btn" disabled={busy}>Continue</Button>
      <p className="ob-signin__foot"><button type="button" className="ob-link" onClick={onBack}>Not you?</button></p>
    </form>
  );
}

/* ---------- the decision ---------- */

type Context = { workspace: Workspace; existing: Site | null; endpoints: { url: string; projects: string[]; siteId: string | null }[] };
/** the chosen workspace as the decision needs it: its projects and plan, the site connected before (Approve rotates its key), the systems
 *  answering customer questions (an agent reads none of the last two: the page then refuses before Approve anyway) */
function useContext(slug: string, site: string): Context | null {
  const [ctx, setCtx] = useState<Context | null>(null);
  useEffect(() => {
    let live = true; setCtx(null);
    void (async () => {
      const workspace = await fetchWorkspace(slug);
      const sites = await api.get<{ rows: Site[] }>(`/api/v1/w/${encodeURIComponent(slug)}/sites`).catch(() => ({ rows: [] as Site[] }));
      const ints = await api.get<{ endpoints: { url: string; projects: string[]; siteId: string | null }[] }>(`/api/v1/w/${encodeURIComponent(slug)}/integrations`).catch(() => ({ endpoints: [] }));
      if (live) setCtx({ workspace, existing: sites.rows.find((s) => s.url === site) ?? null, endpoints: ints.endpoints });
    })();
    return () => { live = false; };
  }, [slug, site]);
  return ctx;
}
const firstChoice = (me: Me) => me.workspaces.find((w) => w.slug === me.lastWorkspace && w.role !== "agent") ?? me.workspaces.find((w) => w.role !== "agent") ?? me.workspaces[0]!;

/** The decision: which workspace and project, the question about another system, what the site may do, Approve or Cancel. */
function Approve({ me, request: r, madeHere }: { me: Me; request: ConnectRequest; /** the workspace this connect flow just made */ madeHere: string | null }) {
  const [slug, setSlug] = useState(madeHere ?? firstChoice(me).slug);
  const ctx = useContext(slug, r.site); // the same home address only: another folder on the host is another site
  const label = labelOf(r);
  const [picked, setPicked] = useState<string | null>(null); // null: the proposal
  const [take, setTake] = useState<boolean | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const live = (ctx?.workspace.projects ?? []).filter((p) => !p.archivedAt);
  // a site connected before keeps its project; a workspace with one project proposes it; else a new project from the site
  const proposed = ctx?.existing?.projects.find((id) => live.some((p) => p.id === id)) ?? (live.length === 1 ? live[0]!.id : "new");
  const project = picked ?? proposed;
  // a site connected at sign-up names the first project (2026-10-24): the workspace made here offers its only project under the site's name
  const firstProject = slug === madeHere && live.length === 1 && project === live[0]!.id;
  const holder = ctx ? factsHolder(project === "new" ? null : project, ctx.endpoints, r.site) : null;
  const room = ctx && project === "new" ? projectRoom({ name: ctx.workspace.plan.name, maxProjects: ctx.workspace.plan.limits.projects }, live.length) : null;
  const allowed = !!ctx && capsOf(ctx.workspace).has("projectSettings");
  const sole = holder && take === true && project !== "new" && takeoverEmpties(holder, project) ? soleHolderProblem(endpointLabel(holder.url)) : null; // the takeover rule (M14 round three)
  const blocked = !ctx || !allowed || !!room || (holder !== null && take === null) || !!sole || busy;
  const chooseWorkspace = (s: string) => { setSlug(s); setPicked(null); setTake(null); setProblem(null); };
  const chooseProject = (p: string) => { setPicked(p); setTake(null); setProblem(null); };

  const approve = async () => {
    setBusy(true); setProblem(null);
    try {
      const done = await api.post<ConnectApproved>("/api/v1/connect", { site: r.site, name: r.name, scopes: r.scopes, events: r.events, returnTo: r.returnTo, state: r.state, challenge: r.challenge, workspace: slug, project, ...(firstProject ? { firstProject: true } : {}), ...(holder ? { takeEndpoint: take } : {}) });
      window.location.assign(done.redirect);
    } catch (e) { setProblem(e instanceof ApiError ? e.message : "Something went wrong. Try again."); setBusy(false); }
  };

  const chosen = me.workspaces.find((w) => w.slug === slug) ?? firstChoice(me);
  const choosers = <SiteChoosers me={me} slug={slug} onWorkspace={chooseWorkspace} project={project} onProject={chooseProject} projects={slug === madeHere && live.length === 1 ? [{ id: live[0]!.id, name: r.name }] : live} newName={r.name} ready={!!ctx}
    adminOnly={ctx && !allowed ? ctx.workspace.name : null} room={room} />;
  const notices = (ctx?.existing || holder || sole) ? <>
    {ctx?.existing && <Notice icon="key"><strong>{label} is already connected.</strong> Approving gives it a new key; the old one stops once the site has it.</Notice>}
    {holder && <Question project={live.find((p) => p.id === project)?.name ?? r.name} host={label} holder={endpointLabel(holder.url)} take={take} onTake={setTake} />}
    {sole && <p className="ob-connect__problem" role="alert">{sole}</p>}
  </> : undefined;
  const notYou = async () => { await signOutHere(); window.location.reload(); }; // the site's request lives after "#" and stays in the address
  return (
    <ConsentPage
      asker={{ name: label, host: true, icon: "web" }}
      workspace={{ name: chosen.name, mark: chosen.mark }}
      title={`Let ${label} into ${chosen.name}?`}
      choosers={choosers} notices={notices}
      can={[{ text: PROJECT_LINE }, ...r.scopes.map((id) => ({ text: PUBLIC_API_SCOPE_LABELS[id], hover: id }))]}
      hears={r.events.map(webhookEventLabel)}
      strip={{ warning: "Only approve if you started this from your site just now." }}
      problem={problem}
      actions={{ primary: ctx?.existing ? "Approve and rotate the key" : "Approve", onPrimary: () => void approve(), disabled: blocked, onCancel: () => window.location.assign(returnCancelled(r)) }}
      signedInAs={{ email: me.user.email, onNotYou: () => void notYou() }}
      footnote={`Approve sends you back to ${label} with a one-time code; the key never travels in an address.`}
    />
  );
}
/** where the site's questions go: the workspace and the project, the browser's own drop-downs (the plugin's flows pick them by label), each
 *  with the line that blocks Approve under it */
function SiteChoosers(p: { me: Me; slug: string; onWorkspace: (slug: string) => void; project: string; onProject: (id: string) => void; projects: { id: string; name: string }[]; newName: string; ready: boolean; adminOnly: string | null; room: string | null }) {
  return <>
    <div className="ob-connect__group">
      <label className="ob-connect__label" htmlFor="connect-workspace">Workspace</label>
      <select id="connect-workspace" className="ob-connect__select" value={p.slug} onChange={(e) => p.onWorkspace(e.target.value)}>
        {p.me.workspaces.map((w) => <option key={w.id} value={w.slug} disabled={w.role === "agent"}>{w.role === "agent" ? `${w.name} · only an administrator connects sites` : w.name}</option>)}
      </select>
      {p.adminOnly && <p className="ob-connect__problem" role="alert">Only an administrator of {p.adminOnly} connects sites.</p>}
    </div>
    <div className="ob-connect__group">
      <label className="ob-connect__label" htmlFor="connect-project">Project</label>
      <select id="connect-project" className="ob-connect__select" value={p.project} onChange={(e) => p.onProject(e.target.value)} disabled={!p.ready}>
        {p.projects.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        <option value="new">New project from this site: {p.newName}</option>
      </select>
      {p.room && <p className="ob-connect__problem" role="alert">{p.room}</p>}
    </div>
  </>;
}
/** the first line of what a site can do: the project rule in plain words (the brief: no "keeps working when you leave", question 11) */
const PROJECT_LINE = "Works only in the project you pick; its key belongs to the workspace";
/** the site's name on the page: "host/folder" for a site in a subfolder, the host for a root site (as the WordPress card reads it) */
const labelOf = (r: Pick<ConnectRequest, "site" | "host">): string => siteLabel(r.site) ?? r.host;

/** "Answer customer questions for <project> from <host>?": never taken from another system without the answer (S2) */
function Question({ project, host, holder, take, onTake }: { project: string; host: string; holder: string; take: boolean | null; onTake: (v: boolean) => void }) {
  return (
    <fieldset className="ob-connect__group ob-connect__question">
      <legend className="ob-connect__label">Answer customer questions for {project} from {host}?</legend>
      <p className="ob-connect__intro">{holder} tells us about this project&apos;s customers today.</p>
      <div className="ob-connect__choices">
        <label className="ob-connect__choice"><input type="radio" name="take" checked={take === true} onChange={() => onTake(true)} />Yes, ask {host} instead</label>
        <label className="ob-connect__choice"><input type="radio" name="take" checked={take === false} onChange={() => onTake(false)} />No, keep {holder}</label>
      </div>
    </fieldset>
  );
}
